/**
 * Price Service
 * Service für die Kommunikation mit der Backend-Preisverwaltung
 */


export function productCodeFromName(product: string): string {
  const map: Record<string, string> = {
    'SkyVision FIXED': 'skyvision-fixed',
    'SkyVision COMFORT': 'skyvision-comfort',
    'SkyVision CIRCULAR': 'skyvision-circular',
    'SkyVision CIRCULAR WALK-ON': 'skyvision-circular-walkon',
    'SkyVision WALKON': 'skyvision-walkon',
    'SkyVision LINEAR': 'skyvision-linear',
    'SkyVision LINEAR WALK-ON': 'skyvision-linear-walkon',
    'SkyVision ACCESS': 'skyvision-access',
  };
  const raw = (product || '').trim();
  if (map[raw]) return map[raw];
  // "WALK-ON" im Anzeigenamen darf nicht zu "...-walk-on" werden —
  // die Backend-Codes heißen skyvision-linear-walkon / circular-walkon.
  const slug = raw.toLowerCase().replace(/\s+/g, '-').replace(/walk-on/g, 'walkon');
  if (Object.values(map).includes(slug)) return slug;
  return slug;
}

interface PriceCalculationConfig {
  product: string;
  dimensions: {
    width: number;
    length: number;
    height: number;
    diameter?: number;
  };
  glassType: 'double' | 'triple' | 'none';
  accessories?: string[];
  ralColorOutside?: string;
  ralColorInside?: string;
  controlType?: 'wcc103' | 'wcc106' | null; // Für COMFORT
}

interface PriceBreakdownItem {
  item: string;
  price: number;
  type?: string;
}

interface PriceCalculationResult {
  total_price: number;
  breakdown: PriceBreakdownItem[];
  currency: string;
}

interface Accessory {
  id: string;
  accessory_code?: string;
  name: string;
  description: string;
  price_type: 'fixed' | 'per_sqm' | 'percentage' | 'conditional' | 'per_unit';
  base_price: number;
  glass_type_restriction: 'double' | 'triple' | 'both';
  category: string;
  category_name?: string;
  is_required?: boolean | number | string;
  is_default?: boolean | number | string;
  min_quantity?: number;
  max_quantity?: number;
}

function normalizeAccessories(data: unknown): Accessory[] {
  if (!Array.isArray(data)) return [];
  return data.map((raw) => {
    const item = raw as Accessory & { category_name?: string };
    const code = String(item.accessory_code || item.id || '');
    return {
      ...item,
      id: code,
      accessory_code: code,
      category: item.category || item.category_name || '',
    };
  });
}

interface GlazingType {
  id: number;
  glazing_code: string;
  name: string;
  description: string;
  glass_layers: number;
  substructure_height: number;
  u_value: number;
  surcharge_percentage: number;
  surcharge_fixed: number;
  info_text: string;
  display_order: number;
  active: boolean;
}

interface RalColor {
  ral_code: string;
  name: string;
  color_hex: string | null;
  surcharge: number;
  location: 'outside' | 'inside' | 'both';
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

class PriceService {
  private baseUrl: string;
  private cache: Map<string, CacheEntry<any>>;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

  constructor() {
    // @ts-ignore - WordPress global
    this.baseUrl = typeof window !== 'undefined' && window.wpApiSettings 
      ? window.wpApiSettings.root + 'skyvision/v1'
      : '/wp-json/skyvision/v1';
    this.cache = new Map();
  }

  /**
   * Generiert einen Cache-Key aus der Konfiguration
   */
  private generateCacheKey(config: PriceCalculationConfig): string {
    return JSON.stringify({
      product: config.product,
      dimensions: config.dimensions,
      glassType: config.glassType,
      accessories: config.accessories ? [...config.accessories].sort() : [],
      ralColorOutside: config.ralColorOutside,
      ralColorInside: config.ralColorInside,
      controlType: config.controlType
    });
  }

  /**
   * Prüft ob ein Cache-Eintrag noch gültig ist
   */
  private isCacheValid<T>(entry: CacheEntry<T>): boolean {
    const now = Date.now();
    return now - entry.timestamp < entry.expiresIn;
  }

  /**
   * Holt Daten aus dem Cache
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && this.isCacheValid(entry)) {
      console.log('Cache hit:', key);
      return entry.data as T;
    }
    
    // Cache-Eintrag abgelaufen oder nicht vorhanden
    if (entry) {
      this.cache.delete(key);
    }
    return null;
  }

  /**
   * Speichert Daten im Cache
   */
  private setCache<T>(key: string, data: T, expiresIn: number = this.CACHE_DURATION): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn
    });
  }

  /**
   * Löscht den gesamten Cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Löscht Cache-Einträge für ein bestimmtes Produkt
   */
  clearProductCache(productCode: string): void {
    const keysToDelete: string[] = [];
    
    // Finde alle Cache-Keys, die das Produkt betreffen
    this.cache.forEach((_, key) => {
      if (key.includes(productCode) || key.includes(`"product":"${productCode}"`)) {
        keysToDelete.push(key);
      }
    });
    
    // Lösche gefundene Keys
    keysToDelete.forEach(key => this.cache.delete(key));
    
    console.log(`Cache cleared for product: ${productCode} (${keysToDelete.length} entries)`);
  }

  /**
   * Löscht Cache-Einträge für Zubehör
   */
  clearAccessoriesCache(productCode?: string): void {
    if (productCode) {
      this.cache.delete(`accessories_${productCode}`);
      console.log(`Accessories cache cleared for: ${productCode}`);
    } else {
      // Lösche alle Zubehör-Caches
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith('accessories_')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
      console.log(`All accessories caches cleared (${keysToDelete.length} entries)`);
    }
  }

  /**
   * Löscht Cache-Einträge für RAL-Farben
   */
  clearRalColorsCache(): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith('ral_colors_')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`RAL colors cache cleared (${keysToDelete.length} entries)`);
  }

  /**
   * Löscht Cache-Einträge für Verglasungstypen
   */
  clearGlazingTypesCache(productCode?: string): void {
    if (productCode) {
      this.cache.delete(`glazing_types_${productCode}`);
      console.log(`Glazing types cache cleared for: ${productCode}`);
    } else {
      // Lösche alle Verglasungstypen-Caches
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith('glazing_types_')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
      console.log(`All glazing types caches cleared (${keysToDelete.length} entries)`);
    }
  }

  /**
   * Berechnet den vollständigen Preis über die Backend-API (mit Caching)
   */
  async calculatePrice(config: PriceCalculationConfig): Promise<PriceCalculationResult> {
    const normalized: PriceCalculationConfig = {
      ...config,
      product: productCodeFromName(config.product),
    };
    const cacheKey = this.generateCacheKey(normalized);

    const cachedResult = this.getFromCache<PriceCalculationResult>(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    const response = await fetch(`${this.baseUrl}/calculate-price`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product: normalized.product,
        dimensions: normalized.dimensions,
        glass_type: normalized.glassType,
        accessories: normalized.accessories || [],
        ral_color_outside: normalized.ralColorOutside,
        ral_color_inside: normalized.ralColorInside,
        control_type: normalized.controlType,
      }),
    });

    let payload: PriceCalculationResult & { message?: string } | null = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(
        payload?.message ||
          `Preis konnte nicht geladen werden (${response.status}).`,
      );
    }

    if (!payload || typeof payload.total_price !== 'number') {
      throw new Error('Backend hat keinen gültigen Preis geliefert.');
    }

    this.setCache(cacheKey, payload);
    return payload;
  }

  /**
   * Holt verfügbare Zubehör-Optionen für ein Produkt (mit Caching)
   */
  async getAccessories(
    productCode: string,
    options: { refresh?: boolean } = {},
  ): Promise<Accessory[]> {
    const code = productCodeFromName(productCode);
    const cacheKey = `accessories_${code}`;

    if (!options.refresh) {
      const cachedResult = this.getFromCache<Accessory[]>(cacheKey);
      if (cachedResult) {
        return cachedResult;
      }
    }

    const urls = [
      `${this.baseUrl}/products/${code}/accessories`,
      `${this.baseUrl}/accessories/${code}`,
    ];

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const normalized = normalizeAccessories(await response.json());
        this.setCache(cacheKey, normalized, 10 * 60 * 1000);
        return normalized;
      } catch (error) {
        console.error('Error fetching accessories:', error);
      }
    }

    return [];
  }

  /** Synchroner Katalog-Lookup aus dem Cache (ohne Netzwerk). */
  peekAccessories(productCode: string): Accessory[] | null {
    return this.getFromCache<Accessory[]>(
      `accessories_${productCodeFromName(productCode)}`,
    );
  }

  /**
   * Holt verfügbare RAL-Farben (mit Caching)
   */
  async getRalColors(location?: 'outside' | 'inside'): Promise<RalColor[]> {
    const cacheKey = `ral_colors_${location || 'all'}`;
    
    // Prüfe Cache
    const cachedResult = this.getFromCache<RalColor[]>(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    try {
      const url = location 
        ? `${this.baseUrl}/ral-colors?location=${location}`
        : `${this.baseUrl}/ral-colors`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('API Error:', response.statusText);
        return [];
      }

      const data = await response.json();
      
      // Cache für 30 Minuten (RAL-Farben ändern sich sehr selten)
      this.setCache(cacheKey, data, 30 * 60 * 1000);
      
      return data;

    } catch (error) {
      console.error('Error fetching RAL colors:', error);
      return [];
    }
  }

  /**
   * Holt verfügbare Verglasungstypen für ein Produkt (mit Caching)
   */
  async getGlazingTypes(productCode: string): Promise<GlazingType[]> {
    const cacheKey = `glazing_types_${productCode}`;
    
    // Prüfe Cache
    const cachedResult = this.getFromCache<GlazingType[]>(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    try {
      const response = await fetch(`${this.baseUrl}/glazing-types/${productCode}`);
      
      if (!response.ok) {
        console.error('API Error:', response.statusText);
        return [];
      }

      const data = await response.json();
      
      // Cache für 10 Minuten (Verglasungstypen ändern sich selten)
      this.setCache(cacheKey, data, 10 * 60 * 1000);
      
      return data;

    } catch (error) {
      console.error('Error fetching glazing types:', error);
      return [];
    }
  }

}

// Singleton-Instanz exportieren
export const priceService = new PriceService();

// Zusätzlich Named Exports für Kompatibilität
export default priceService;

